import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { RootState } from '@reduxjs/toolkit/query';
import { CacheTag } from '~/store/services/CacheTag';

export class NotesPaginationCacheManager {
  private readonly dispatch: ThunkDispatch<any, any, UnknownAction>;

  private readonly getState: () => RootState<any, any, 'api'>;

  private readonly notesApi;

  constructor(
    dispatch: ThunkDispatch<any, any, UnknownAction>,
    getState: () => RootState<any, any, 'api'>,
    notesApi,
  ) {
    this.dispatch = dispatch;
    this.getState = getState;
    this.notesApi = notesApi;
  }

  private dispatchUpdate(
    inquiryId: string,
    pageNumber: number,
    updateFn: (draft: GetPatientInquiryNotesResponse) => void,
    pageSize: number,
  ) {
    this.dispatch(
      this.notesApi.util.updateQueryData(
        'getPatientInquiryNotes',
        { inquiryId, notesPageNumber: pageNumber, notesPageSize: pageSize },
        updateFn,
      ),
    );
  }

  handleNewNoteCache(
    draft: GetPatientInquiryNotesResponse,
    body: PersistNoteRequest | UpdateNoteRequest,
    noteExternalId: string | null,
  ) {
    const newNote = {
      ...body,
      activityDate: body.activityDate ?? new Date().toISOString(),
      activityWith: body?.activityWith.map((activityWith) => ({
        activityExternalId: '',
        activityWithExternalId: '',
        activityWithId: 0,
        activityWithName: activityWith.activityWithName ?? '',
        isActive: true,
        noteId: 0,
        otherName: '',
      })),
      addendumContent: '',
      appointmentExternalId: '',
      attachments: 'attachments' in body ? body.attachments : [],
      authoredByProviderExternalId: body.authoredByProviderExternalId ?? '',
      createdBy: '',
      createdDateTime: new Date().toISOString(),
      isConfidential: false,
      lastModifiedDateTime: new Date().toISOString(),
      lastModifiedProviderExternalId: '',
      noteCommunicationMethod: body.noteCommunicationMethod ?? '',
      noteContent: body.noteContent ?? '',
      noteExternalId,
      noteStatus: body.noteStatus ?? '',
      noteTemplateRevisionData: body.noteTemplateRevisionData,
      noteTemplateRevisionId: body.noteTemplateRevisionId,
      noteTemplateRevisionSurveyJsId: body.noteTemplateRevisionSurveyJsId,
      noteType: body.noteType ?? '',
      patientExternalId:
        'patientExternalId' in body ? body.patientExternalId : '',
      tags: [],
      title: body.title ?? '',
      updatedBy: '',
    };

    const existingIndex = draft.data.findIndex(
      (note) => note.noteExternalId === newNote.noteExternalId,
    );

    if (existingIndex !== -1) {
      draft.data[existingIndex] = newNote;
    } else {
      draft.data.unshift(newNote);
      draft.count += 1;
    }
  }

  handleVoidNoteCache(
    draft: GetPatientInquiryNotesResponse,
    inquiryId: string,
    noteExternalId: string,
  ) {
    draft.data = draft.data.filter(
      (note) => note.noteExternalId !== noteExternalId,
    );
    draft.count -= 1;

    if (draft.pageNumber !== 1 || draft.data.length >= draft.pageSize) return;

    const nextPageNotes = this.notesApi.endpoints.getPatientInquiryNotes.select(
      {
        inquiryId,
        notesPageNumber: draft.pageNumber + 1,
        notesPageSize: draft.pageSize,
      },
    )(this.getState())?.data;

    if (nextPageNotes?.data.length) {
      const [nextNote] = nextPageNotes.data;
      this.dispatchUpdate(
        inquiryId,
        draft.pageNumber + 1,
        (draft) => {
          if (draft?.data) draft.data.push(nextNote);
        },
        draft.pageSize,
      );
    }
  }

  updateInquiryLastContactDate(inquiryId: string) {
    return (draft: GetInquiriesViewsResponse) => {
      const inquiry = draft?.data?.find(
        (inquiry) => inquiry.inquiryId === inquiryId,
      );

      if (inquiry) {
        inquiry.lastContactDate = new Date().toISOString();
      }
    };
  }

  reloadPatientInquiryNotes(inquiryId: string) {
    this.dispatch(
      this.notesApi.endpoints.getPatientInquiryNotes.initiate({
        inquiryId,
        notesPageNumber: 1,
        notesPageSize: 10,
      }),
    );
    this.dispatch(
      this.notesApi.util.invalidateTags([
        { id: inquiryId, type: CacheTag.PatientInquiryNotes },
      ]),
    );
  }
}
